// Standard Library Imports
import React, { useEffect, useState, useContext } from 'react';
//External library imports
import { toast } from 'react-toastify';
import { UilImport } from '@iconscout/react-unicons';
import _ from 'lodash';
import moment from 'moment';

//Internal module imports
import { useLoader, useQueryParamWithDefault } from '../../../../hooks';
import economicsClasses from '../Economics.module.css';
import MonthPicker from '../../../../components/Inputs/MonthPicker/MonthPicker';
import { DropdownComponent } from '../../../../components/Inputs/Input';
import Table from '../../../../components/Table/Table';
import { RootEconomicsService } from '../../../../services/BiddingService/Root/EconomicsService';
import { dkCurrencyFormat } from '../../../../utils/currencyHelper';
import { AuthContext } from '../../../../context/AuthContext';
import { ROOT_USER_ROLES, ADMIN_USER_ROLES } from '../../../../constants';
import { AdminEconomicsService } from '../../../../services/BiddingService/Admin/EconomicsService';
import { DateParam } from 'use-query-params';
import Typography from '../../../../components/Typography/Typography';
import IconComponent from '../../../../components/Icon/Icon';
import { exportData } from '../../../../utils/downloadHelper';

const TABLE_HEADERS_KEYS = {
    root: {
        headers: ['Partner', 'Customer', 'Chargers Count', 'Revenue (kr.)'],
        keys: ['admin', 'customer', 'chargersCount', 'cost'],
    },
    admin: {
        headers: ['Customer', 'Chargers Count', 'Revenue (kr.)'],
        keys: ['customer', 'chargersCount', 'cost'],
    },
};

const FixedChargers = ({ cId, admins = [], customers = [], selectedAdmin, setSelectedAdmin, selectedCustomer, setSelectedCustomer }) => {
    const [startLoader, stopLoader] = useLoader();
    const [date, setDate] = useQueryParamWithDefault('month', DateParam, moment().startOf('month').toDate(), cId, { clearOnUnmount: false });
    const { state } = useContext(AuthContext);
    const userRole = _.get(state, ['user', 'userRole']);
    const isRootUser = ROOT_USER_ROLES.includes(userRole);
    const isAdminUser = ADMIN_USER_ROLES.includes(userRole);
    const [tableHeaderAndKey, setTableHeaderAndKey] = useState(isRootUser ? TABLE_HEADERS_KEYS.root : isAdminUser ? TABLE_HEADERS_KEYS.admin : {});
    const [data, setData] = useState({
        data: [],
        count: 0,
        totalCost: 0,
    });

    const [paginationQuery, setPaginationQuery] = useState({
        skip: 0,
        page: 0,
        limit: 15,
    });
    const [initialLoad, setInitialLoad] = useState(false);

    useEffect(() => {
        if (document.readyState === 'complete') setInitialLoad(true);
    }, []);

    useEffect(() => {
        if (selectedCustomer && date && initialLoad) getData();
    }, [paginationQuery.skip, selectedCustomer, selectedAdmin, initialLoad]);

    const handlePageChange = (pageNo) => {
        setPaginationQuery((prev) => ({
            ...prev,
            skip: prev.limit * pageNo,
            page: pageNo,
        }));
    };

    const getData = (handler = null, paginatedData = true) => {
        const successHandler = handler ? handler : handleSuccess;
        const params = {
            month: moment(date).format('YYYY-MM'),
            customerId: selectedCustomer,
            ...(isRootUser ? { adminId: selectedAdmin } : {}),
            ...(paginatedData ? { limit: paginationQuery.limit, skip: paginationQuery.skip } : {}),
        };

        let service;
        if (isRootUser) service = RootEconomicsService;
        if (isAdminUser) service = AdminEconomicsService;

        service.GetFixedChargersRevenue(
            params,
            () => startLoader('getData'),
            successHandler,
            handleError,
            () => stopLoader('getData')
        );
    };

    const handleSuccess = ({ data }) => {
        const processedData = _.get(data, ['data', 'data'], []).map((el) => ({
            admin: el.adminName,
            customer: el.customerName,
            chargersCount: el.uniqueChargerIdCount,
            cost: dkCurrencyFormat(el.cost || 0),
        }));

        setData({
            data: processedData,
            count: _.get(data, ['data', 'totalCount', 0, 'count'], 0),
            totalCost: _.get(data, ['data', 'totalCost'], 0),
        });
    };

    const handleDownload = ({ data }) => {
        const processedData = _.get(data, ['data', 'data'], []).map((el) => ({
            partner: el.adminName,
            customer: el.customerName,
            chargersCount: el.uniqueChargerIdCount,
            cost: el.cost || 0,
        }));

        exportData(processedData, 'FixedChargers', ['partner', 'customer', 'chargersCount', 'cost'], {
            partner: 'Total',
            customer: '',
            chargersCount: '',
            cost: _.get(data, ['data', 'totalCost'], 0),
        });
    };

    const handleError = (err) => {
        toast.error(err?.response?.data?.message);
    };

    return (
        <div>
            <div className={economicsClasses.FilterBar}>
                <div className={economicsClasses.FilterContainer}>
                    {/* left container */}
                    <div className={economicsClasses.SubContainer}>
                        {isRootUser ? (
                            <>
                                <div className={economicsClasses.DropDownContainer}>
                                    <label>Admin</label>
                                    <DropdownComponent
                                        name="adminId"
                                        options={admins}
                                        defaultValue={{ value: selectedAdmin }}
                                        onChange={(e) => {
                                            if (e.value !== selectedAdmin) setSelectedAdmin(e.value);
                                        }}
                                    />
                                </div>
                            </>
                        ) : (
                            ''
                        )}

                        <div className={economicsClasses.DropDownContainer}>
                            <label>Customer</label>
                            <DropdownComponent
                                name="customerId"
                                options={customers}
                                defaultValue={{ value: selectedCustomer }}
                                onChange={(e) => {
                                    if (e.value !== selectedCustomer) {
                                        setSelectedCustomer(e.value);
                                    }
                                }}
                            />
                        </div>
                    </div>

                    {/* right container */}
                    <div className={economicsClasses.SubContainer}>
                        {date && (
                            <div className={economicsClasses.DropDownContainer}>
                                <label>Month</label>
                                <MonthPicker date={date} setDate={setDate} />
                            </div>
                        )}

                        <div style={{ marginTop: '0.8vh' }} onClick={() => getData()}>
                            <button className="btn-primary">Submit</button>
                        </div>

                        <div style={{ marginTop: '3vh' }} onClick={() => getData(handleDownload, false)}>
                            <IconComponent icon={UilImport} tooltip={'Download'} size={'1.5vw'} />
                        </div>
                    </div>
                </div>
            </div>
            <div className={economicsClasses.TableContainer}>
                <div className={economicsClasses.FilterContainer} style={{ marginTop: '1vh' }}>
                    <div className={economicsClasses.SubContainer}>
                        <div className={economicsClasses.TableCount}>
                            Total Revenue :
                            <span>
                                <Typography size="14" content={data.totalCost + ' kr.'} />
                            </span>
                        </div>

                        <div className={economicsClasses.TableCount}>
                            Total Count :
                            <span>
                                <Typography size="14" content={data.count || 0} />
                            </span>
                        </div>
                    </div>
                </div>

                <Table
                    head={tableHeaderAndKey.headers}
                    keys={tableHeaderAndKey.keys}
                    data={data.data}
                    page={paginationQuery.page}
                    Pagination={true}
                    limit={paginationQuery.limit}
                    handlePageChange={handlePageChange}
                    totalCount={data.count}
                />
            </div>
        </div>
    );
};

export default FixedChargers;
